// Mirrors src/schemas/reviewRequest.schema.ts in klydo-api. Keep the two in
// step when a value is added.

export type ReviewRepo =
  | "api"
  | "admin"
  | "studio"
  | "functions"
  | "shopify"
  | "clock";

export type ReviewPolicy = "any" | "all";
export type ReviewAudience = "reviewers" | "channel";
export type ReviewPriority = "normal" | "urgent";
export type ReviewRequestStatus =
  | "open"
  | "changes_requested"
  | "approved"
  | "closed";
export type ReviewOutcome = "merged" | "cancelled";
export type ReviewClosedBy = "github" | "user";
export type ReviewerStatus = "pending" | "approved" | "changes_requested";
export type PullState = "open" | "merged" | "closed";
export type PullReviewState = "approved" | "changes_requested";

export type ReviewEventType =
  | "created"
  | "re_requested"
  | "reviewer_added"
  | "reviewer_removed"
  | "review_submitted"
  | "review_dismissed"
  | "unmapped_reviewer"
  | "pull_merged"
  | "pull_closed"
  | "pull_reopened"
  | "new_commits"
  | "reminded"
  | "escalated"
  | "snoozed"
  | "cancelled"
  | "manually_checked"
  | "manual_check_removed"
  | "synced";

export type PullReview = {
  githubLogin: string;
  userId: string | null;
  state: PullReviewState;
  submittedAt: string;
};

export type ReviewPull = {
  repo: ReviewRepo;
  githubRepo: string;
  number: number;
  url: string;
  state: PullState;
  headSha: string;
  hasNewCommitsSinceRound: boolean;
  reviews: PullReview[];
};

export type Reviewer = {
  userId: string;
  status: ReviewerStatus;
  respondedAt: string | null;
  remindCount: number;
  lastRemindedAt: string | null;
  snoozedUntil: string | null;
};

// A review done outside GitHub. Counts as an approval from `userId`.
export type ManualCheck = {
  userId: string;
  byUserId: string;
  at: string;
};

export type ReviewEvent = {
  type: ReviewEventType;
  at: string;
  actorUserId: string | null;
  actorLogin: string | null;
  repo: ReviewRepo | null;
  pullNumber: number | null;
  detail: string;
};

export type ReviewRequest = {
  id: string;
  title: string;
  description: string;
  prs: ReviewPull[];
  ticketId: string | null;
  ticketKey: string;
  requesterId: string;
  reviewers: Reviewer[];
  policy: ReviewPolicy;
  // Missing on requests created before team requests existed.
  audience?: ReviewAudience;
  slackThread?: { channel: string; ts: string } | null;
  // Missing on requests created before manual checks existed.
  manualChecks?: ManualCheck[];
  priority: ReviewPriority;
  status: ReviewRequestStatus;
  outcome: ReviewOutcome | null;
  closedBy: ReviewClosedBy | null;
  round: number;
  roundStartedAt: string;
  nextRemindAt: string | null;
  events: ReviewEvent[];
  approvedAt: string | null;
  closedAt: string | null;
  createdAt: string;
  updatedAt: string;
};

export const REVIEW_REPOS: { value: ReviewRepo; label: string }[] = [
  { value: "api", label: "API" },
  { value: "admin", label: "Admin" },
  { value: "studio", label: "Studio" },
  { value: "functions", label: "Functions" },
  { value: "shopify", label: "Shopify" },
  { value: "clock", label: "Clock" },
];

export const REPO_LABEL: Record<ReviewRepo, string> = Object.fromEntries(
  REVIEW_REPOS.map(({ value, label }) => [value, label]),
) as Record<ReviewRepo, string>;

export const REVIEW_STATUS_LABEL: Record<ReviewRequestStatus, string> = {
  open: "Waiting",
  changes_requested: "Changes requested",
  approved: "Approved",
  closed: "Closed",
};

export const REVIEW_STATUS_COLOR: Record<ReviewRequestStatus, string> = {
  open: "warning.main",
  changes_requested: "error.main",
  approved: "success.main",
  closed: "grey.500",
};

export const REVIEWER_STATUS_COLOR: Record<ReviewerStatus, string> = {
  pending: "grey.500",
  approved: "success.main",
  changes_requested: "error.main",
};

export const REVIEWER_STATUS_LABEL: Record<ReviewerStatus, string> = {
  pending: "Pending",
  approved: "Approved",
  changes_requested: "Changes requested",
};

export const PULL_STATE_LABEL: Record<PullState, string> = {
  open: "Open",
  merged: "Merged",
  closed: "Closed",
};

export const POLICY_LABEL: Record<ReviewPolicy, string> = {
  any: "Any one of them",
  all: "All of them",
};

export const OUTCOME_LABEL: Record<ReviewOutcome, string> = {
  merged: "Merged",
  cancelled: "Cancelled",
};

export type ReviewQuickView = "to-review" | "mine" | "open" | "closed";

export const REVIEW_QUICK_VIEWS: { value: ReviewQuickView; label: string }[] = [
  { value: "to-review", label: "To review" },
  { value: "mine", label: "Requested by me" },
  { value: "open", label: "All open" },
  { value: "closed", label: "Closed" },
];

export const SNOOZE_OPTIONS_HOURS = [2, 4, 24];
